/**
 * Tenant configuration registry (PROD-568).
 *
 * Parses every config against `tenantConfigSchema` at module load. Per the
 * 2026-05-05 fixer round (COMPONENT-CONTRACTS §2 demo-gap field):
 *   - A config that fails Zod validation does NOT throw at module load.
 *   - Instead the registry emits a sentinel `{ id, missing: true, error }`
 *     entry so the TenantProfileSwitcher can render the missing tenant as
 *     a disabled option with the canonical
 *     `[FIXTURE MISSING - Tenant '...' is not available in Demo Mode.
 *     Restore the fixture or contact ops.]` tooltip.
 *
 * This preserves the data-integrity gate (a developer who breaks a config
 * still sees a console warning + the disabled UI surface) while honoring
 * the master v3.3 amendment requirement: a missing fixture must surface a
 * visible warning, never a silent fallback.
 *
 * Tests: `tests/tenant-configs.test.ts` covers the happy path;
 * `tests/tenant-profile-switcher.test.tsx` covers the missing-fixture
 * sentinel render.
 *
 * Phase 2 wires `getTenantConfig` into the LLM prompt builder and the RAG
 * retriever per master Pre-flight Multi-Clinic Divergence Mechanism.
 */
import { tenantConfigSchema, type TenantConfig, type TenantId } from './types';
import { apexCardiometabolic } from './apex-cardiometabolic';
import { longevityHealthInstitute } from './longevity-health-institute';

/**
 * Canonical display names for every known tenant id. The sentinel
 * fallback uses these so the switcher can label the disabled option even
 * when the underlying config is missing.
 */
export const TENANT_DISPLAY_NAMES: Record<TenantId, string> = {
  'apex-cardiometabolic': 'Apex Cardiometabolic Center',
  'longevity-health-institute': 'Longevity Health Institute',
};

/**
 * Sentinel emitted when a tenant config fails schema validation. The
 * switcher renders this as a disabled option with the canonical fixture-
 * missing tooltip; downstream consumers (LLM prompt builder, RAG
 * retriever) treat `missing` as a hard guard.
 */
export interface MissingTenantConfig {
  readonly id: TenantId;
  readonly displayName: string;
  readonly missing: true;
  readonly error: string;
}

export type TenantRegistryEntry = TenantConfig | MissingTenantConfig;

const RAW_REGISTRY: ReadonlyArray<{ id: TenantId; raw: unknown }> = [
  { id: 'apex-cardiometabolic', raw: apexCardiometabolic },
  { id: 'longevity-health-institute', raw: longevityHealthInstitute },
];

function parseRegistryEntry(entry: {
  id: TenantId;
  raw: unknown;
}): TenantRegistryEntry {
  const parsed = tenantConfigSchema.safeParse(entry.raw);
  if (parsed.success) return parsed.data;
  // Surface the schema error in the dev console so the developer sees
  // why the tenant fell back to the disabled sentinel.
  console.warn(
    `[tenant-configs] Config for "${entry.id}" failed schema validation; rendering as missing.`,
    parsed.error.flatten(),
  );
  return {
    id: entry.id,
    displayName: TENANT_DISPLAY_NAMES[entry.id],
    missing: true,
    error: parsed.error.message,
  };
}

/**
 * Full tenant registry in canonical order. Always contains every known
 * tenant id; entries are either a valid `TenantConfig` or a
 * `MissingTenantConfig` sentinel.
 */
export const TENANT_REGISTRY: ReadonlyArray<TenantRegistryEntry> =
  RAW_REGISTRY.map(parseRegistryEntry);

/**
 * Valid tenant configs only (filters out sentinels). Backward-compatible
 * with the prior export shape so callers that do not need the sentinels
 * (e.g., `tests/tenant-configs.test.ts`) keep working.
 */
export const TENANT_CONFIGS: ReadonlyArray<TenantConfig> =
  TENANT_REGISTRY.filter(
    (entry): entry is TenantConfig => !('missing' in entry),
  );

/** Type guard - is this registry entry a missing-fixture sentinel? */
export function isMissingTenantConfig(
  entry: TenantRegistryEntry,
): entry is MissingTenantConfig {
  return 'missing' in entry && entry.missing === true;
}

/** Resolve a config by tenant id. Returns `undefined` if no valid config matches. */
export function getTenantConfig(id: TenantId): TenantConfig | undefined {
  return TENANT_CONFIGS.find((c) => c.id === id);
}

/** First valid config in canonical order; default selection on first paint. */
export function getDefaultTenantConfig(): TenantConfig {
  return TENANT_CONFIGS[0];
}

export type { TenantConfig } from './types';
export {
  tenantConfigSchema,
  clinicalPriorityWeightingSchema,
  nbaFrequencySchema,
} from './types';
